import React, { useEffect, useMemo, useState } from 'react';
import { isValidMarcacaoId } from '../config/prospeccaoMarcacao';
import cccApi from '../api/cccApi';
import ComercialManagement from '@crm/components/Comercial/ComercialManagement';
import '@crm/components/Comercial/ComercialManagement.css';

type ProspeccaoLeadSeed = {
  id: string;
  company: string;
  contactName: string;
  phone: string;
  email: string;
  googleMapsLink?: string;
};

type MarcacaoSeed = {
  id?: number;
  marcacao_id?: string;
  nome_empresa?: string;
  nome_responsavel?: string;
  morada?: string;
  contacto_cliente?: string;
  email?: string;
  form_data?: Record<string, unknown>;
  [key: string]: unknown;
};

type Props = {
  lead: ProspeccaoLeadSeed;
  marcacaoId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const swallowOverlayBackdropPointer = (e: React.MouseEvent) => {
  if (e.target === e.currentTarget) {
    e.stopPropagation();
  }
};

const CccSubmeterContratoProspeccaoOverlay: React.FC<Props> = ({
  lead,
  marcacaoId,
  onClose,
  onSuccess,
}) => {
  const [seed, setSeed] = useState<Record<string, MarcacaoSeed> | null>(null);
  const resolvedMarcacaoId = useMemo(
    () => (isValidMarcacaoId(marcacaoId) ? marcacaoId.trim() : ''),
    [marcacaoId]
  );

  const embeddedContractApi = useMemo(
    () => ({
      uploadFile: async (formData: FormData) => {
        try {
          return await cccApi.uploadProspeccaoContractFile(lead.id, formData);
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string; error?: string } } };
          const msg =
            ax.response?.data?.message ||
            ax.response?.data?.error ||
            (err instanceof Error ? err.message : 'Falha ao enviar ficheiro');
          return { success: false, error: msg, message: msg };
        }
      },
      persist: async (contractData: Record<string, unknown>) => {
        try {
          const res = await cccApi.submitProspeccaoContract(lead.id, contractData);
          if (!res.success) {
            throw new Error(res.message || 'Erro ao salvar dados do contrato');
          }
        } catch (err: unknown) {
          const ax = err as { response?: { data?: { message?: string } } };
          throw new Error(
            ax.response?.data?.message ||
              (err instanceof Error ? err.message : 'Erro ao salvar dados do contrato')
          );
        }
      },
    }),
    [lead.id]
  );

  useEffect(() => {
    if (!resolvedMarcacaoId) return;
    let cancelled = false;

    const fallbackSeed: Record<string, MarcacaoSeed> = {
      [resolvedMarcacaoId]: {
        marcacao_id: resolvedMarcacaoId,
        nome_empresa: lead.company,
        nome_responsavel: lead.contactName,
        contacto_cliente: lead.phone,
        email: lead.email,
        morada: lead.googleMapsLink || '',
        empresa: lead.company,
        responsavel: lead.contactName,
        contacto: lead.phone,
      },
    };

    const normalizeSeedEntry = (raw: MarcacaoSeed): MarcacaoSeed => ({
      ...raw,
      marcacao_id: raw.marcacao_id || resolvedMarcacaoId,
      empresa: raw.nome_empresa || raw.empresa || lead.company,
      responsavel: raw.nome_responsavel || raw.responsavel || lead.contactName,
    });

    (async () => {
      try {
        const data = await cccApi.getProspeccaoLeadMarcacao(lead.id);
        if (!cancelled && data?.success && data.marcacao) {
          setSeed({
            [resolvedMarcacaoId]: normalizeSeedEntry(data.marcacao as MarcacaoSeed),
          });
          return;
        }
      } catch {
        /* fallback to lead payload */
      }
      if (!cancelled) {
        setSeed(fallbackSeed);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [lead, resolvedMarcacaoId]);

  const seedReady = useMemo(() => seed !== null, [seed]);

  if (!resolvedMarcacaoId) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">
          <p>Marcação inválida — não foi possível abrir Submeter Contrato.</p>
          <button type="button" className="rec-btn rec-btn-ghost" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    );
  }

  if (!seedReady) {
    return (
      <div className="rec-solicitar-proposta-overlay" role="presentation">
        <div className="rec-solicitar-proposta-loading">A carregar formulário…</div>
      </div>
    );
  }

  return (
    <div
      className="rec-solicitar-proposta-overlay"
      role="presentation"
      onMouseDown={swallowOverlayBackdropPointer}
    >
      <ComercialManagement
        embeddedContractOnly
        embeddedContractMarcacaoId={resolvedMarcacaoId}
        embeddedProspeccaoLeadId={lead.id}
        embeddedContractApi={embeddedContractApi}
        embeddedMarcacaoSeed={seed as Record<string, never>}
        onEmbeddedContractClose={onClose}
        onEmbeddedContractSuccess={onSuccess}
      />
    </div>
  );
};

export default CccSubmeterContratoProspeccaoOverlay;
